/**
 * API Endpoint Constants
 * All API paths in one place for maintainability
 */

export const API_ENDPOINTS = {
  // Gateway auth endpoints (no /api/v1 prefix)
  AUTH: {
    ME: '/auth/me',
    REFRESH: '/auth/refresh',
    AUTHORIZE: '/auth/authorize',
    LOGOUT: '/auth/logout',
  },
  // Gateway account management endpoints (no /api/v1 prefix)
  ACCOUNT: {
    EMAIL: '/auth/account/email',
    PASSWORD: '/auth/account/password',
  },
  // Task API endpoints
  TASKS: {
    BASE: '/api/v1/tasks',
    MAP: '/api/v1/tasks/map',
    METADATA: '/api/v1/tasks/metadata',
    BY_ID: (id: string) => `/api/v1/tasks/${id}` as const,
    BY_SHORT_ID: (shortId: string) =>
      `/api/v1/tasks/by-short-id/${shortId}` as const,
    STATUS: (id: string) => `/api/v1/tasks/${id}/status` as const,
  },
  REQUIREMENTS: {
    BASE: (taskId: string) => `/api/v1/tasks/${taskId}/requirements` as const,
    BY_ID: (taskId: string, id: string) =>
      `/api/v1/tasks/${taskId}/requirements/${id}` as const,
  },
  ATTACHMENTS: {
    BASE: (taskId: string) => `/api/v1/tasks/${taskId}/attachments` as const,
    DOWNLOAD: (id: string) => `/api/v1/attachments/${id}` as const,
    PREVIEW: (id: string) => `/api/v1/attachments/${id}/preview` as const,
    DELETE: (taskId: string, id: string) =>
      `/api/v1/tasks/${taskId}/attachments/${id}` as const,
  },
  COMMENTS: {
    BASE: (taskId: string) => `/api/v1/tasks/${taskId}/comments` as const,
    BY_ID: (taskId: string, id: string) =>
      `/api/v1/tasks/${taskId}/comments/${id}` as const,
  },
  HISTORY: {
    BASE: (taskId: string) => `/api/v1/tasks/${taskId}/history` as const,
  },
  NOTE_HISTORY: {
    BASE: (noteId: string) => `/api/v1/notes/${noteId}/history` as const,
  },
  // Identity service endpoints
  SETTINGS: {
    BASE: '/api/v1/users/me/settings',
    BY_KEY: (key: string) => `/api/v1/users/me/settings/${key}` as const,
    BATCH: '/api/v1/users/me/settings/batch',
  },
  RECENT_SEARCHES: {
    BASE: '/api/v1/users/me/recent-searches',
    BY_ID: (id: string) => `/api/v1/users/me/recent-searches/${id}` as const,
  },
  USERS: {
    CONTEXT: '/api/v1/users/me/context',
    PREFERENCES: '/api/v1/users/me/preferences',
    PROFILE: '/api/v1/users/me/profile',
    AVATAR: '/api/v1/users/me/avatar',
    DELETE_ACCOUNT: '/api/v1/users/me/account',
  },
  INVITATIONS: {
    BY_TOKEN: (token: string) => `/api/v1/invitations/${token}` as const,
    ACCEPT: (token: string) => `/api/v1/invitations/${token}/accept` as const,
    DECLINE: (token: string) => `/api/v1/invitations/${token}/decline` as const,
  },
  // Workspace management endpoints (API service)
  // Dashboard API endpoints
  DASHBOARD: {
    METRICS: '/api/v1/dashboard/metrics',
    SUMMARY: '/api/v1/dashboard/summary',
    TASK_PROGRESS: '/api/v1/dashboard/task-progress',
    RECENT_TASKS: '/api/v1/dashboard/recent-tasks',
    DELAYED_TASKS: '/api/v1/dashboard/delayed-tasks',
  },
  WORKSPACES: {
    // Workspace CRUD
    LIST: '/api/v1/workspaces',
    CREATE: '/api/v1/workspaces',
    UPDATE: (id: string) => `/api/v1/workspaces/${id}` as const,
    DELETE: (id: string) => `/api/v1/workspaces/${id}` as const,
    BY_SHORT_ID: (shortId: string) =>
      `/api/v1/workspaces/by-short-id/${shortId}` as const,
    // Members management
    MEMBERS: (id: string) => `/api/v1/workspaces/${id}/members` as const,
    MEMBER_ROLE: (workspaceId: string, userId: string) =>
      `/api/v1/workspaces/${workspaceId}/members/${userId}/role` as const,
    REMOVE_MEMBER: (workspaceId: string, userId: string) =>
      `/api/v1/workspaces/${workspaceId}/members/${userId}` as const,
    // Invitations management
    INVITATIONS: (id: string) =>
      `/api/v1/workspaces/${id}/invitations` as const,
    RESEND_INVITATION: (workspaceId: string, invitationId: string) =>
      `/api/v1/workspaces/${workspaceId}/invitations/${invitationId}/resend` as const,
    CANCEL_INVITATION: (workspaceId: string, invitationId: string) =>
      `/api/v1/workspaces/${workspaceId}/invitations/${invitationId}` as const,
  },
  // Export API endpoints
  EXPORT: {
    GEOJSON: '/api/v1/export/geojson',
  },
  // Map Layers API endpoints
  LAYERS: {
    BASE: '/api/v1/layers',
    BY_ID: (id: string) => `/api/v1/layers/${id}` as const,
    GEOJSON: (id: string) => `/api/v1/layers/${id}/geojson` as const,
    DOWNLOAD: (id: string) => `/api/v1/layers/${id}/download` as const,
    PREFERENCES: '/api/v1/layers/preferences',
  },
  // Notes API endpoints
  NOTES: {
    BASE: '/api/v1/notes',
    MAP: '/api/v1/notes/map',
    BY_ID: (id: string) => `/api/v1/notes/${id}` as const,
    BY_SHORT_ID: (shortId: string) =>
      `/api/v1/notes/by-short-id/${shortId}` as const,
    ATTACHMENTS: (noteId: string) =>
      `/api/v1/notes/${noteId}/attachments` as const,
    DELETE_ATTACHMENT: (noteId: string, attachmentId: string) =>
      `/api/v1/notes/${noteId}/attachments/${attachmentId}` as const,
    DOWNLOAD_ATTACHMENT: (attachmentId: string) =>
      `/api/v1/note-attachments/${attachmentId}` as const,
    PREVIEW_ATTACHMENT: (attachmentId: string) =>
      `/api/v1/note-attachments/${attachmentId}/preview` as const,
    UPLOAD_IMAGE: (noteId: string) =>
      `/api/v1/notes/${noteId}/upload-image` as const,
    BY_TASK_ID: (taskId: string) => `/api/v1/tasks/${taskId}/notes` as const,
  },
  // Roads autocomplete API endpoints
  ROADS: {
    BASE: '/api/v1/roads',
  },
  // Obstructions API endpoints
  OBSTRUCTIONS: {
    BASE: '/api/v1/obstructions',
    MAP: '/api/v1/obstructions/map',
    STATS: '/api/v1/obstructions/stats',
    BY_ID: (id: string) => `/api/v1/obstructions/${id}` as const,
    BY_SHORT_ID: (shortId: string) =>
      `/api/v1/obstructions/short/${shortId}` as const,
    STATUS: (id: string) => `/api/v1/obstructions/${id}/status` as const,
    GEOMETRIES: (obstructionId: string) =>
      `/api/v1/obstructions/${obstructionId}/geometries` as const,
    GEOMETRY_BY_ID: (obstructionId: string, geometryId: string) =>
      `/api/v1/obstructions/${obstructionId}/geometries/${geometryId}` as const,
    SIGNS: (obstructionId: string) =>
      `/api/v1/obstructions/${obstructionId}/signs` as const,
    SIGN_BY_ID: (obstructionId: string, signId: string) =>
      `/api/v1/obstructions/${obstructionId}/signs/${signId}` as const,
    EXPORT_GEOJSON: '/api/v1/obstructions/export/geojson',
    HISTORY: (obstructionId: string) =>
      `/api/v1/obstructions/${obstructionId}/history` as const,
  },
  // Obstruction attachments API endpoints
  OBSTRUCTION_ATTACHMENTS: {
    BASE: (obstructionId: string) =>
      `/api/v1/obstructions/${obstructionId}/attachments` as const,
    DOWNLOAD: (id: string) => `/api/v1/obstructions/attachments/${id}` as const,
    PREVIEW: (id: string) =>
      `/api/v1/obstructions/attachments/${id}/preview` as const,
    DELETE: (obstructionId: string, id: string) =>
      `/api/v1/obstructions/${obstructionId}/attachments/${id}` as const,
  },
  // Authenticated share-link management (workspace-scoped)
  SHARE_LINKS: {
    BASE: '/api/v1/share-links',
    BY_ID: (id: string) => `/api/v1/share-links/${id}` as const,
    REVOKE: (id: string) => `/api/v1/share-links/${id}/revoke` as const,
    REACTIVATE: (id: string) => `/api/v1/share-links/${id}/reactivate` as const,
  },
  // Public, unauthenticated share endpoints (token-based, no workspace header)
  PUBLIC_SHARES: {
    BY_TOKEN: (token: string) => `/api/v1/public/shares/${token}` as const,
    VERIFY_PASSWORD: (token: string) =>
      `/api/v1/public/shares/${token}/verify-password` as const,
    LIST: (token: string) => `/api/v1/public/shares/${token}/list` as const,
    MAP: (token: string) => `/api/v1/public/shares/${token}/map` as const,
    TASK_DETAIL: (token: string, id: string) =>
      `/api/v1/public/shares/${token}/tasks/${id}` as const,
    NOTE_DETAIL: (token: string, id: string) =>
      `/api/v1/public/shares/${token}/notes/${id}` as const,
    OBSTRUCTION_DETAIL: (token: string, id: string) =>
      `/api/v1/public/shares/${token}/obstructions/${id}` as const,
    CUSTOM_LAYER_GEOJSON: (token: string, layerId: string) =>
      `/api/v1/public/shares/${token}/custom-layers/${layerId}/geojson` as const,
    VIEW: (token: string) => `/api/v1/public/shares/${token}/view` as const,
    ATTACHMENT_DOWNLOAD: (token: string, attachmentId: string) =>
      `/api/v1/public/shares/${token}/attachments/${attachmentId}` as const,
    ATTACHMENT_PREVIEW: (token: string, attachmentId: string) =>
      `/api/v1/public/shares/${token}/attachments/${attachmentId}/preview` as const,
    NOTE_ATTACHMENT_DOWNLOAD: (token: string, attachmentId: string) =>
      `/api/v1/public/shares/${token}/note-attachments/${attachmentId}` as const,
    NOTE_ATTACHMENT_PREVIEW: (token: string, attachmentId: string) =>
      `/api/v1/public/shares/${token}/note-attachments/${attachmentId}/preview` as const,
  },
} as const;

/**
 * SignalR Configuration
 */
export const SIGNALR_CONFIG = {
  /** Comments hub path */
  HUB_PATH: '/hubs/comments',
  /** Presence hub path */
  PRESENCE_HUB_PATH: '/hubs/presence',
} as const;

/**
 * Presence Configuration
 */
export const PRESENCE_CONFIG = {
  /** Heartbeat interval when tab is visible (ms) */
  HEARTBEAT_VISIBLE_INTERVAL: 30_000,
  /** Heartbeat interval when tab is hidden (ms) */
  HEARTBEAT_HIDDEN_INTERVAL: 45_000,
} as const;

/**
 * Upload Configuration
 */
export const UPLOAD_CONFIG = {
  /** Maximum file size in bytes (25MB) */
  MAX_FILE_SIZE_BYTES: 25 * 1024 * 1024,
  /** Allowed MIME types for uploads */
  ALLOWED_MIME_TYPES: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
} as const;
